import React, {useEffect, useRef, useState} from "react";
import {Form, Input, Modal, Spin, Tree} from 'antd';
import { ObjTest, treeUtil } from 'jzfz-web-utils';
import {File3Icon} from "../icon/Icon";
import {request} from "../../util/request";
import {GLOBAL_LABEL_MANAGE_API} from "../../config/api";
import CustomEmpty from "../customEmpty/CustomEmpty";
import './labelModal.less';


const { Search } = Input;
const { getListFromTree } = treeUtil;


interface LabelFormProps {
    defaultCheckedKeys?: string[];   // 默认选中结点key
    loading?: boolean;
    visible: boolean;
    checkedDisabled?: boolean;  // 是否将选中的禁用
    onSubmit: (labelList: any[]) => void;
    onCancel: () => void;
}


/**
 * 添加标签弹窗组件
 * @param props
 * @constructor
 */
const LabelModal = (props: LabelFormProps) => {
    const { defaultCheckedKeys = [], visible, checkedDisabled, onSubmit, onCancel, loading: submitLoading } = props;
    const [form] = Form.useForm();
    const { resetFields, getFieldsValue, setFieldsValue, validateFields } = form;
    const [treeData, setTreeData] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
    const [disabledKeys, setDisabledKeys] = useState<string[]>([]);
    const treeDataCacheRef = useRef<any[]>([]);


    useEffect(() => {
        if (visible) {
            getTree();
        }
    }, [visible])


    /**
     * 获取选中结点key
     * @param list
     * @param checkedKeys
     */
    const getCheckedKeys = (list: any[], checkedKeys: string[]) => {
        list.forEach(v => {
            if (ObjTest.isNotEmptyArray(v.children)) {
                getCheckedKeys(v.children, checkedKeys);
                let flag = v.children.every(child => checkedKeys.includes(child.key));
                if (flag) {
                    checkedKeys.push(v.key);
                }
            }
        })
    }


    /**
     * 获取展开结点key
     * @param list
     * @param expandedKeys
     * @param checkedKeys
     */
    const getExpandedKeys = (list: any[], expandedKeys: string[], checkedKeys: string[]) => {
        list.forEach(v => {
            if (ObjTest.isNotEmptyArray(v.children)) {
                getExpandedKeys(v.children, expandedKeys, checkedKeys);
                let flag = v.children.some(child => checkedKeys.includes(child.key) || expandedKeys.includes(child.key));
                if (flag) {
                    expandedKeys.push(v.key);
                }
            }
        })
    }


    /**
     * 重新构造树
     * @param list
     */
    const buildTree = (list: any[]) => {
        list.forEach((v: any) => {
            let type = v.menuId && v.labelId ? 2 : 1;   // 1: 标签组 2:标签
            v.type = type;
            v.title = type === 1 ? v.menuName : v.labelName;
            v.key = v.labelId && v.menuId ? v.labelId : v.menuId;
            if (ObjTest.isNotEmptyArray(v.children)) {
                buildTree(v.children);
            }
        })
    }


    /**
     * 获取标签组、标签树
     */
    const getTree = async () => {
        let params = await validateFields();
        setLoading(true);
        request('/labelmenu/tree', params, {baseURL: GLOBAL_LABEL_MANAGE_API}).then((res: any) => {
            buildTree(res.data);
            setTreeData(res.data);

            // 设置选中结点key、展开结点key
            treeDataCacheRef.current = getListFromTree(res.data || []);
            let keys = treeDataCacheRef.current.filter((v: any) => defaultCheckedKeys.includes(v.key))
                .map((v: any) => v.key);
            let checkedKeys = [...keys];
            let expandedKeys = [];
            getCheckedKeys(res.data, checkedKeys);
            getExpandedKeys(res.data, expandedKeys, checkedKeys);
            setCheckedKeys(checkedKeys);
            setExpandedKeys(expandedKeys);
            if (checkedDisabled) {
                setDisabledKeys(checkedKeys);
            }
        }).finally(() => {
            setLoading(false);
        })
    }


    /**
     * 获取所有子节点的key
     * @param list
     */
    const getChildrenKeys = (list: any[]) => {
        let tempList = [];
        list.forEach((v: any) => {
            tempList = [...tempList, v.key];
            if (ObjTest.isNotEmptyArray(v.children)) {
                tempList = [...tempList, ...getChildrenKeys(v.children)];
            }}
        )
        return tempList;
    }


    /**
     * 勾选结点
     * @param info
     */
    const checkHandle = (checkedKeys: string[]) => {
        setCheckedKeys(checkedKeys);
    }


    /**
     * 展开树结点
     */
    const expandHandle = (expandedKeys: any[]) => {
        setExpandedKeys(expandedKeys);
    }


    /**
     * 搜索标签
     * @param value
     */
    const onSearch = (value: string) => {
        getTree();
    }


    /**
     * 提交表单
     */
    const submit = () => {
        let labelInfos = treeDataCacheRef.current
            .filter((v: any) => checkedKeys.includes(v.key) && v.type === 2)
            .map((v: any) => {
                return {labelId: v.key, labelName: v.labelName, menuName: v.menuName}
            });
        onSubmit(labelInfos);
    }


    const loop = (list: any[]): any[] => list.map((v: any) => {
        const { title, key, type } = v;
        if (ObjTest.isNotEmptyArray(v.children)) {
            return {
                title,
                key,
                disabled: disabledKeys.includes(key),
                icon: type === 2 ? <File3Icon style={{color: '#1887F1'}} /> : null,
                children: loop(v.children),
            }
        }
        return {
            title,
            key,
            disabled: disabledKeys.includes(key),
            icon: type === 2 ? <File3Icon style={{color: '#1887F1'}} /> : null,
        }
    })


    return (
        <Modal
            wrapClassName="common-label-tree-modal-wrap"
            forceRender={true}
            maskClosable={false}
            title={(
                <div className="flexic flexjs">
                    <span>添加标签</span>
                    <Form form={form}>
                        <Form.Item name="searchKey" noStyle>
                            <Search className="search-input" placeholder="查询标签组、标签" allowClear onSearch={onSearch} enterButton />
                        </Form.Item>
                    </Form>
                </div>
            )}
            closable={false}
            visible={visible}
            confirmLoading={loading || submitLoading}
            afterClose={() => {
                resetFields();
            }}
            onOk={submit}
            onCancel={onCancel}
            okButtonProps={{className: 'button-red'}}
        >
            <div style={{minHeight: 200, maxHeight: 600, overflow: "auto"}}>
                <Spin spinning={loading}>
                    {
                        ObjTest.isNotEmptyArray(treeData) ? (
                            <Tree
                                height={600}
                                checkable
                                selectable={false}
                                showIcon
                                checkedKeys={checkedKeys}
                                expandedKeys={expandedKeys}
                                treeData={loop(treeData)}
                                onCheck={checkHandle}
                                onExpand={expandHandle}
                            />
                        ) : <CustomEmpty text="暂无数据" />
                    }
                </Spin>
            </div>
        </Modal>
    )
}


export default LabelModal;
